import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, AlertTriangle, LogIn, Users } from "lucide-react";
import { toast } from "sonner";

export default function InviteAccept() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [accepted, setAccepted] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/invite/${token}`);
            setInfo(data);
            setError(null);
        } catch (e) {
            setError(e?.response?.data?.detail || "Invite not found or expired");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await api.post(`/invite/${token}/accept`, {});
            setAccepted(true);
            await refresh();
            toast.success("You're in — all six modules unlocked");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Accept failed");
        } finally {
            setAccepting(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-neutral-500" data-testid="invite-loading">Loading invite…</div>;
    }

    if (error) {
        return (
            <div className="max-w-lg mx-auto px-6 py-24" data-testid="invite-error">
                <div className="p-6 border border-red-300 bg-red-50 rounded-lg flex items-start gap-4">
                    <AlertTriangle className="text-red-600 mt-1" size={22} />
                    <div>
                        <h1 className="text-xl font-bold text-neutral-900 mb-2">Invite not available</h1>
                        <p className="text-sm text-neutral-700 mb-4">{error}</p>
                        <Link to="/" className="text-sm underline text-blue-700">Return home</Link>
                    </div>
                </div>
            </div>
        );
    }

    const inviteEmail = info?.seat?.email;
    const ownerEmail = info?.license?.owner_email;
    const licenseTitle = info?.license?.title;
    const modules = info?.license?.modules || [];
    const currentEmail = (user?.email || "").toLowerCase();
    const emailMismatch = user && currentEmail !== inviteEmail;

    if (accepted) {
        return (
            <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="invite-accepted">
                <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={48} />
                <h1 className="text-3xl font-black text-neutral-900 mb-2">You're in.</h1>
                <p className="text-neutral-600 mb-8">Your seat is active. All six modules are unlocked on your dashboard.</p>
                <button
                    onClick={() => navigate("/dashboard")}
                    data-testid="invite-go-dashboard"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-blue-800"
                >
                    Go to dashboard →
                </button>
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto px-6 py-16" data-testid="invite-page">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-blue-700 font-semibold mb-3">
                <Users size={14} /> {licenseTitle || "School Site License"}
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-neutral-900 leading-tight mb-4">You've been invited.</h1>
            <p className="text-neutral-600 mb-8">
                {ownerEmail ? <><strong>{ownerEmail}</strong> invited you to </> : "You've been invited to "}
                a School Site License seat at <strong>ai4faculty.info</strong>. Accepting unlocks the full six-module curriculum on your dashboard.
            </p>

            <div className="p-6 border border-neutral-200 rounded-xl bg-white mb-6" data-testid="invite-details">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Invite email</div>
                <div className="text-lg font-mono font-semibold text-neutral-900 mb-4" data-testid="invite-email">{inviteEmail}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Modules included</div>
                <div className="text-sm text-neutral-700">{modules.length} modules · full curriculum · Modules 1–6</div>
            </div>

            {!user && (
                <div className="p-4 border border-blue-200 bg-blue-50 rounded-lg mb-4 flex items-start gap-3" data-testid="invite-signin-prompt">
                    <LogIn className="text-blue-700 mt-0.5" size={18} />
                    <div className="text-sm text-neutral-800">
                        Sign in with <strong>{inviteEmail}</strong> to accept this invite.
                        <div className="mt-2 flex gap-2">
                            <Link
                                to={`/login?next=${encodeURIComponent(`/invite/${token}`)}`}
                                className="inline-block bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-blue-800"
                                data-testid="invite-signin-btn"
                            >
                                Sign in
                            </Link>
                            <Link
                                to={`/register?next=${encodeURIComponent(`/invite/${token}`)}`}
                                className="inline-block border border-blue-700 text-blue-700 text-xs font-semibold px-4 py-2 rounded-full hover:bg-blue-50"
                                data-testid="invite-signup-btn"
                            >
                                Create account
                            </Link>
                        </div>
                    </div>
                </div>
            )}

            {user && emailMismatch && (
                <div className="p-4 border border-amber-300 bg-amber-50 rounded-lg mb-4 flex items-start gap-3" data-testid="invite-email-mismatch">
                    <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
                    <div className="text-sm text-neutral-800">
                        You're signed in as <strong>{currentEmail}</strong>, but this invite was sent to <strong>{inviteEmail}</strong>. Sign out and sign in with the invited email.
                    </div>
                </div>
            )}

            {user && !emailMismatch && (
                <button
                    onClick={handleAccept}
                    disabled={accepting}
                    data-testid="invite-accept-btn"
                    className="inline-flex items-center gap-2 bg-blue-700 text-white text-base font-semibold px-6 py-3 rounded-full hover:bg-blue-800 disabled:opacity-40"
                >
                    {accepting ? "Accepting…" : "Accept and unlock modules"}
                </button>
            )}
        </div>
    );
}
